import {
  Engine,
  WebGPUEngine,
  Scene,
  HemisphericLight,
  FreeCamera,
  Vector3,
  type AbstractEngine,
} from "@babylonjs/core";

export interface BootstrapResult {
  engine: AbstractEngine;
  backend: "webgpu" | "webgl2";
}

/**
 * Initializes the best available Babylon.js engine for this browser:
 * WebGPU when supported, otherwise a WebGL2 fallback. Both backends run the
 * same Scene code (PBR materials, WaterMaterial, SkyMaterial, post-processing
 * all work on either), so the fallback is a real degrade path, not a stub.
 *
 * `WebGPUEngine.IsSupportedAsync` only checks API *presence* — it can return
 * true in environments where the adapter/device/swap-chain still fails at
 * creation time (observed in headless/software-rendered Chromium: the
 * adapter reports supported, `initAsync()` resolves cleanly, but the very
 * first submitted frame fails swap-chain texture creation and the context
 * is immediately and permanently lost — surfaced asynchronously, after
 * `initAsync()` already resolved, not as a rejection we can catch). So a
 * short render probe follows init and falls back to WebGL2 if the context
 * is lost within that window, rather than leaving a black screen.
 *
 * This also matters beyond headless testing: this app is meant to run
 * unattended for very long stretches (screensaver-style), where real GPU
 * context loss (driver reset, laptop sleep/wake, eGPU unplug) is a genuine,
 * if rare, occurrence — so treating context loss as a recoverable signal
 * rather than an uncaught fatal error is the right default regardless.
 */
export async function createEngine(canvas: HTMLCanvasElement): Promise<BootstrapResult> {
  if (await WebGPUEngine.IsSupportedAsync) {
    const webgpu = await tryCreateWebGPUEngine(canvas);
    if (webgpu) return webgpu;
    console.warn("[Castaway] WebGPU failed during startup; falling back to WebGL2.");
  }

  const engine = new Engine(canvas, true, {
    antialias: true,
    adaptToDeviceRatio: true,
    preserveDrawingBuffer: true,
    stencil: true,
  });
  return { engine, backend: "webgl2" };
}

async function tryCreateWebGPUEngine(canvas: HTMLCanvasElement): Promise<BootstrapResult | null> {
  let engine: WebGPUEngine;
  try {
    engine = new WebGPUEngine(canvas, { antialias: true, adaptToDeviceRatio: true });
    await engine.initAsync();
  } catch (err) {
    console.warn("[Castaway] WebGPU reported supported but failed to initialize.", err);
    return null;
  }

  const lostDuringProbe = await probeWebGPURender(engine);
  if (lostDuringProbe) {
    engine.dispose();
    return null;
  }
  return { engine, backend: "webgpu" };
}

/**
 * Renders a couple of throwaway frames and gives the context a brief window
 * to fail. Deliberately driven through `engine.runRenderLoop()` (stopped
 * after a couple of ticks) rather than a single bare `scene.render()` call:
 * a bare call outside the loop's normal acquire/present/retire cadence left
 * the swap-chain's backbuffer texture in a state where the *next* scene's
 * first real frame referenced an already-destroyed probe texture (a
 * "Destroyed texture ... used in a submit" validation warning). Matching
 * the real render-loop lifecycle here avoids that handoff glitch.
 */
function probeWebGPURender(engine: WebGPUEngine): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (lost: boolean) => {
      if (settled) return;
      settled = true;
      lostObserver.remove();
      resolve(lost);
    };

    const lostObserver = engine.onContextLostObservable.add(() => finish(true));

    let probeScene: Scene;
    try {
      probeScene = new Scene(engine);
      probeScene.activeCamera = new FreeCamera("probeCamera", new Vector3(0, 0, -5), probeScene);
      new HemisphericLight("probeLight", Vector3.Up(), probeScene);
    } catch (err) {
      console.warn("[Castaway] WebGPU probe scene setup threw.", err);
      finish(true);
      return;
    }

    let framesRendered = 0;
    engine.runRenderLoop(() => {
      try {
        probeScene.render();
      } catch (err) {
        console.warn("[Castaway] WebGPU probe render threw.", err);
        engine.stopRenderLoop();
        finish(true);
        return;
      }

      framesRendered++;
      if (framesRendered >= 2) {
        engine.stopRenderLoop();
        probeScene.dispose();
        // One more animation-frame tick so the swap-chain fully cycles past
        // the probe's last presented texture before the real scene renders.
        requestAnimationFrame(() => finish(false));
      }
    });
  });
}
